import Colors from '@root/joinroot.com/src/utils/colors';
import Link from '@root/joinroot.com/src/components/core/link';
import LinkButtonModel from '@root/joinroot.com/src/models/link-button';
import PropTypes from '@root/vendor/prop-types';
import { StyleSheet } from '@root/core/src/utils/styles';
import { regular, semiBold } from '@root/brand/src/assets/fonts/root-sans/emotion';

export default function LinkButton({
  buttonStyles,
  children,
  environmentalizeUrlFnToUse,
  isTabbable = true,
  onClick,
  to,
  type = LinkButtonModel.types.PRIMARY,
}) {
  return (
    <Link
      css={[
        type !== LinkButtonModel.types.TERTIARY && baseStyles.primaryAndSecondary,
        styles[type],
        buttonStyles,
      ]}
      data-testid={'joinroot-link-button'}
      environmentalizeUrlFnToUse={environmentalizeUrlFnToUse}
      isTabbable={isTabbable}
      onClick={onClick}
      to={to}
    >
      {children}
    </Link>
  );
}

LinkButton.propTypes = {
  buttonStyles: PropTypes.oneOfType([PropTypes.object, PropTypes.arrayOf(PropTypes.object)]),
  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
  ]).isRequired,
  environmentalizeUrlFnToUse: PropTypes.func,
  isTabbable: PropTypes.bool,
  onClick: PropTypes.func,
  to: PropTypes.string.isRequired,
  type: PropTypes.oneOf(Object.values(LinkButtonModel.types)).isRequired,
};

const buttonActionColors = {
  // Action state colors are specific to link-button.js so they're stored here rather than a global colors file.
  rootOrangeBtnPressed: () => '#FF7F50',
  rootOrangeBtnDisabled: () => '#DADADA',
};

const baseStyles = StyleSheet.create({
  primaryAndSecondary: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 60,
    minWidth: 220,
    padding: '6px 26px',
    border: `2px solid ${Colors.rootOrange()}`,
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 18,
    letterSpacing: '-0.15px',
    lineHeight: '130%',
    transition: '400ms cubic-bezier(0.16, 0.85, 0.45, 1)',
    userSelect: 'none',
    transform: 'perspective(1000px) translate3d(0, 0, 0)',

    'svg': {
      transition: 'transform 300ms cubic-bezier(.17,.9,.45,1.7)',
    },

    '&:hover': {
      textDecoration: 'none',
      transform: 'perspective(1000px) translate3d(0, 0, 10px)',

      'svg': {
        transform: 'translateX(5px)',
      },
    },
  },
});

const styles = StyleSheet.create({
  primary: {
    ...semiBold(),
    backgroundColor: Colors.rootOrange(),
    boxShadow: `0 8px 6px -6px ${Colors.nearBlack(0)}`,
    color: Colors.white(),

    'svg path': {
      fill: Colors.white(),
    },

    '&:hover': {
      color: Colors.white(),
      boxShadow: `0 8px 6px -6px ${Colors.nearBlack(.3)}`,
    },

    '&:focus': {},

    '&:active': {
      borderColor: buttonActionColors.rootOrangeBtnPressed(),
      backgroundColor: buttonActionColors.rootOrangeBtnPressed(),
    },
  },

  secondary: {
    ...regular(),
    backgroundColor: Colors.white(),
    color: Colors.nearBlack(),

    'svg path': {
      fill: Colors.nearBlack(),
    },

    '&:hover': {
      color: Colors.nearBlack(),
      borderColor: Colors.nearBlack(),
    },

    '&:focus': {},

    '&:active': {
      color: Colors.nearBlack(.7),
      borderColor: buttonActionColors.rootOrangeBtnPressed(),
    },
  },

  tertiary: {
    display: 'flex',
    color: Colors.nearBlack(),
    transition: '200ms ease-out',
    lineHeight: '130%',
    textDecoration: 'underline',

    'svg': {
      marginLeft: 20,
      transition: 'transform 300ms cubic-bezier(.17,.9,.45,1.7)',

      'path': {
        fill: Colors.nearBlack(),
      },
    },

    '&:hover': {
      color: Colors.rootOrange(),

      'svg': {
        transform: 'translateX(5px)',

        'path': {
          fill: Colors.rootOrange(),
        },
      },
    },

    '&:focus': {},

    '&:active': {
      color: buttonActionColors.rootOrangeBtnPressed(),

      'svg path': {
        fill: buttonActionColors.rootOrangeBtnPressed(),
      },
    },
  },
});

